"use client";

import { Phone, Zap } from "lucide-react";
import { useRouter } from "next/navigation";
import type { ScenarioRecommendationCardData } from "../../types/insurabot-cards";

const S = "rgba(0,0,0,0.06) 0px 0px 0px 1px, rgba(0,0,0,0.03) 0px 1px 2px";

const DIFF_COLORS: Record<string, { text: string; bg: string }> = {
  easy: { text: "#059669", bg: "#ecfdf5" },
  medium: { text: "#d97706", bg: "#fffbeb" },
  hard: { text: "#dc2626", bg: "#fef2f2" },
};

export default function ScenarioRecommendationCard({ data }: { data: ScenarioRecommendationCardData }) {
  const router = useRouter();
  if (!data.scenarios || data.scenarios.length === 0) return null;

  return (
    <div className="mt-3 rounded-lg overflow-hidden animate-in fade-in slide-in-from-bottom-2 duration-500" style={{ boxShadow: S }}>
      <div className="bg-white px-4 py-3">
        <div className="flex items-center gap-2 mb-1">
          <Zap className="h-3.5 w-3.5 text-primary" />
          <p className="text-[9px] font-bold text-gray-400 uppercase tracking-[0.1em]">Recommended Drills</p>
        </div>
        <p className="text-[11px] text-gray-500 mb-3">
          Targeting <span className="font-semibold text-gray-800">{data.weakestDimension}</span>
          <span className="font-mono font-bold text-red-500 ml-1" style={{ fontFeatureSettings: '"tnum"' }}>{Math.round(data.weakestScore)}</span>
        </p>

        <div className="space-y-1.5">
          {data.scenarios.map((sc) => {
            const diff = DIFF_COLORS[sc.difficulty] || DIFF_COLORS.medium;
            const imgPath = sc.personaName
              ? `/training/personas/${sc.personaName.toLowerCase().replace(/\s+/g, "-")}.webp`
              : null;

            return (
              <button
                key={sc.id}
                onClick={() => router.push("/training")}
                className="w-full flex items-center gap-2.5 rounded-md p-2 text-left transition-all hover:bg-gray-50 group"
              >
                <div className="h-8 w-8 rounded-full overflow-hidden bg-gray-100 flex-shrink-0">
                  {imgPath && (
                    <img src={imgPath} alt="" className="h-full w-full object-cover" onError={e => { (e.target as HTMLImageElement).style.display = "none"; }} />
                  )}
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-[11px] font-semibold text-gray-800 truncate">{sc.name}</p>
                  <div className="flex items-center gap-1.5 mt-0.5">
                    <span className="text-[9px] text-gray-400">{sc.personaName}</span>
                    <span className="text-[7px] font-bold uppercase px-1 py-px rounded" style={{ color: diff.text, backgroundColor: diff.bg }}>
                      {sc.difficulty}
                    </span>
                  </div>
                </div>
                <Phone className="h-3 w-3 text-gray-300 group-hover:text-primary transition-colors flex-shrink-0" />
              </button>
            );
          })}
        </div>
      </div>
    </div>
  );
}
